/**
 * XACML Parser Utility
 * Parses XACML 3.0 JSON policies into structured format for display and editing
 */

import type {
  ParsedXacmlPolicy,
  ParsedXacmlAttributes,
  ParsedXacmlRule,
  ParsedXacmlCondition,
  ParsedXacmlObligation,
  ParsedXacmlAttributeAssignment,
  XacmlCategory,
  RuleEffect,
} from '@/types/policy'

/**
 * XACML Category URN mapping
 */
export const XACML_CATEGORY_URNS: Record<XacmlCategory, string> = {
  subject: 'urn:oasis:names:tc:xacml:1.0:subject-category:access-subject',
  resource: 'urn:oasis:names:tc:xacml:3.0:attribute-category:resource',
  action: 'urn:oasis:names:tc:xacml:3.0:attribute-category:action',
  environment: 'urn:oasis:names:tc:xacml:3.0:attribute-category:environment',
}

/**
 * Reverse mapping from URN to category
 */
export const URN_TO_CATEGORY: Record<string, XacmlCategory> = Object.entries(
  XACML_CATEGORY_URNS
).reduce(
  (acc, [category, urn]) => {
    acc[urn] = category as XacmlCategory
    return acc
  },
  {} as Record<string, XacmlCategory>
)

/**
 * XACML Data Type URN mapping
 */
export const XACML_DATA_TYPE_URNS: Record<string, string> = {
  string: 'http://www.w3.org/2001/XMLSchema#string',
  boolean: 'http://www.w3.org/2001/XMLSchema#boolean',
  integer: 'http://www.w3.org/2001/XMLSchema#integer',
  double: 'http://www.w3.org/2001/XMLSchema#double',
  date: 'http://www.w3.org/2001/XMLSchema#date',
  time: 'http://www.w3.org/2001/XMLSchema#time',
  dateTime: 'http://www.w3.org/2001/XMLSchema#dateTime',
  anyURI: 'http://www.w3.org/2001/XMLSchema#anyURI',
  base64Binary: 'http://www.w3.org/2001/XMLSchema#base64Binary',
}

/**
 * Reverse mapping from data type URN to simple name
 */
export const URN_TO_DATA_TYPE: Record<string, string> = Object.entries(
  XACML_DATA_TYPE_URNS
).reduce(
  (acc, [name, urn]) => {
    acc[urn] = name
    return acc
  },
  {} as Record<string, string>
)

/**
 * Common XACML function IDs and their display names
 */
export const XACML_FUNCTIONS: Record<string, string> = {
  'urn:oasis:names:tc:xacml:1.0:function:string-equal': 'equals',
  'urn:oasis:names:tc:xacml:1.0:function:string-one-and-only': 'one-and-only',
  'urn:oasis:names:tc:xacml:1.0:function:integer-equal': 'equals',
  'urn:oasis:names:tc:xacml:1.0:function:integer-greater-than': 'greater than',
  'urn:oasis:names:tc:xacml:1.0:function:integer-less-than': 'less than',
  'urn:oasis:names:tc:xacml:1.0:function:boolean-equal': 'equals',
  'urn:oasis:names:tc:xacml:1.0:function:date-equal': 'equals',
  'urn:oasis:names:tc:xacml:1.0:function:time-equal': 'equals',
  'urn:oasis:names:tc:xacml:1.0:function:string-contains': 'contains',
  'urn:oasis:names:tc:xacml:1.0:function:string-starts-with': 'starts with',
  'urn:oasis:names:tc:xacml:1.0:function:string-regexp-match': 'regex match',
  'urn:oasis:names:tc:xacml:1.0:function:and': 'AND',
  'urn:oasis:names:tc:xacml:1.0:function:or': 'OR',
  'urn:oasis:names:tc:xacml:1.0:function:not': 'NOT',
}

/**
 * Get human-readable category display name
 */
export function getCategoryDisplayName(category: XacmlCategory): string {
  const displayNames: Record<XacmlCategory, string> = {
    subject: 'Subject',
    resource: 'Resource',
    action: 'Action',
    environment: 'Environment',
  }
  return displayNames[category] ?? category
}

/**
 * Get category icon name for UI
 */
export function getCategoryIcon(category: XacmlCategory): string {
  const icons: Record<XacmlCategory, string> = {
    subject: 'user',
    resource: 'file',
    action: 'zap',
    environment: 'globe',
  }
  return icons[category] ?? 'circle'
}

/**
 * Get simple data type name from URN
 */
export function getDataTypeName(dataTypeUrn: string): string {
  return URN_TO_DATA_TYPE[dataTypeUrn] ?? dataTypeUrn.split('#').pop() ?? 'unknown'
}

/**
 * Get function display name from URN
 */
export function getFunctionDisplayName(functionUrn: string): string {
  return XACML_FUNCTIONS[functionUrn] ?? functionUrn.split(':').pop() ?? 'unknown'
}

/**
 * Format attribute value for display based on data type
 */
export function formatAttributeValue(
  value: string | number | boolean,
  dataType: string
): string {
  const typeName = getDataTypeName(dataType)

  switch (typeName) {
    case 'boolean':
      return value ? 'true' : 'false'
    case 'date':
    case 'dateTime':
      if (typeof value === 'string') {
        try {
          return new Date(value).toLocaleString()
        } catch {
          return String(value)
        }
      }
      return String(value)
    case 'time':
      return String(value)
    default:
      return String(value)
  }
}

/**
 * Create empty parsed attributes structure
 */
function createEmptyAttributes(): ParsedXacmlAttributes {
  return {
    subject: [],
    resource: [],
    action: [],
    environment: [],
  }
}

/**
 * Parse XACML Target structure to extract attributes by category
 */
export function parseXacmlTarget(
  target: Record<string, unknown> | null | undefined
): ParsedXacmlAttributes {
  const attributes = createEmptyAttributes()

  if (!target) return attributes

  // XACML Target structure: Target -> AnyOf[] -> AllOf[] -> Match[]
  const anyOf = target.AnyOf as Array<Record<string, unknown>> | undefined
  if (!anyOf || !Array.isArray(anyOf)) return attributes

  for (const anyOfItem of anyOf) {
    const allOf = anyOfItem.AllOf as Array<Record<string, unknown>> | undefined
    if (!allOf || !Array.isArray(allOf)) continue

    for (const allOfItem of allOf) {
      const matches = allOfItem.Match as Array<Record<string, unknown>> | undefined
      if (!matches || !Array.isArray(matches)) continue

      for (const match of matches) {
        const attributeDesignator = match.AttributeDesignator as Record<string, unknown> | undefined
        const attributeValue = match.AttributeValue as Record<string, unknown> | undefined

        if (!attributeDesignator) continue

        const categoryUrn = attributeDesignator.Category as string
        const category = URN_TO_CATEGORY[categoryUrn]

        if (!category) continue

        const attributeId = attributeDesignator.AttributeId as string
        const dataType = (attributeDesignator.DataType ?? attributeValue?.DataType ?? '') as string
        const matchId = match.MatchId as string | undefined
        const value = attributeValue?.Value

        // Find existing attribute or create new one
        let existingAttr = attributes[category].find(
          (a) => a.attributeId === attributeId && a.matchId === matchId
        )

        if (!existingAttr) {
          existingAttr = {
            attributeId,
            dataType,
            values: [],
            matchId,
          }
          attributes[category].push(existingAttr)
        }

        // Add value if not already present
        if (value !== undefined && value !== null && !existingAttr.values.includes(value as string | number | boolean)) {
          existingAttr.values.push(value as string | number | boolean)
        }
      }
    }
  }

  return attributes
}

/**
 * Resolve category from an AttributeDesignator's Category URN
 */
function resolveCategory(designator: Record<string, unknown>): XacmlCategory | undefined {
  const categoryUrn = designator.Category as string | undefined
  return categoryUrn ? URN_TO_CATEGORY[categoryUrn] : undefined
}

/**
 * Parse XACML Condition to extract conditions
 */
export function parseXacmlCondition(
  condition: Record<string, unknown> | null | undefined
): ParsedXacmlCondition[] {
  const conditions: ParsedXacmlCondition[] = []

  if (!condition) return conditions

  const apply = condition.Apply as Record<string, unknown> | undefined
  if (!apply) return conditions

  extractConditionsFromApply(apply, conditions, false)

  return conditions
}

/**
 * Recursively extract conditions from XACML Apply function.
 * Detects not() wrappers and propagates negation flag to leaf conditions.
 */
function extractConditionsFromApply(
  apply: Record<string, unknown>,
  conditions: ParsedXacmlCondition[],
  negated: boolean
): void {
  const functionId = apply.FunctionId as string | undefined

  // Detect not() wrapper - toggle negation and recurse into child
  if (functionId === 'urn:oasis:names:tc:xacml:1.0:function:not') {
    const nestedApply = apply.Apply as Array<Record<string, unknown>> | Record<string, unknown> | undefined
    if (Array.isArray(nestedApply)) {
      for (const nested of nestedApply) {
        extractConditionsFromApply(nested, conditions, !negated)
      }
    } else if (nestedApply) {
      extractConditionsFromApply(nestedApply, conditions, !negated)
    }
    return
  }

  // Handle and/or logical connectives - recurse into children
  if (
    functionId === 'urn:oasis:names:tc:xacml:1.0:function:and' ||
    functionId === 'urn:oasis:names:tc:xacml:1.0:function:or'
  ) {
    const nestedApply = apply.Apply as Array<Record<string, unknown>> | undefined
    if (Array.isArray(nestedApply)) {
      for (const nested of nestedApply) {
        extractConditionsFromApply(nested, conditions, negated)
      }
    }
    return
  }

  // Handle direct AttributeDesignator + AttributeValue (leaf)
  const attributeDesignator = apply.AttributeDesignator as Record<string, unknown> | undefined
  const attributeValue = apply.AttributeValue as Record<string, unknown> | undefined

  if (attributeDesignator && attributeValue) {
    conditions.push({
      attributeId: attributeDesignator.AttributeId as string,
      category: resolveCategory(attributeDesignator),
      functionId: functionId ?? 'unknown',
      negated,
      value: attributeValue.Value as string | number | boolean,
      dataType: (attributeDesignator.DataType ?? attributeValue.DataType ?? '') as string,
    })
    return
  }

  // Handle nested Apply functions (e.g., comparison with one-and-only wrapper)
  const nestedApply = apply.Apply as Array<Record<string, unknown>> | Record<string, unknown> | undefined

  if (Array.isArray(nestedApply)) {
    for (const nested of nestedApply) {
      const nestedDesignator = nested.AttributeDesignator as Record<string, unknown> | undefined
      const nestedValue = apply.AttributeValue ?? nested.AttributeValue

      if (nestedDesignator && nestedValue) {
        const valueObj = nestedValue as Record<string, unknown>
        conditions.push({
          attributeId: nestedDesignator.AttributeId as string,
          category: resolveCategory(nestedDesignator),
          functionId: functionId ?? 'unknown',
          negated,
          value: valueObj.Value as string | number | boolean,
          dataType: (nestedDesignator.DataType ?? valueObj.DataType ?? '') as string,
        })
      } else {
        extractConditionsFromApply(nested, conditions, negated)
      }
    }
  } else if (nestedApply) {
    extractConditionsFromApply(nestedApply, conditions, negated)
  }
}

/**
 * Normalization map for legacy obligation IDs to PEP-compatible IDs.
 * Ensures policies saved with old-format IDs are auto-corrected when parsed.
 */
const OBLIGATION_ID_NORMALIZATION: Record<string, string> = {
  'urn:polix:obligation:mask-column': 'urn:polix:obligation:column-mask',
  'urn:polix:obligation:enforce-row-filter': 'urn:polix:obligation:row-filter',
}

/**
 * Normalization map for legacy attribute assignment IDs to PEP-compatible IDs.
 * The PEP enforcer uses simple keys (e.g. "table", "predicate") not full URNs.
 */
const OBLIGATION_ATTR_ID_NORMALIZATION: Record<string, string> = {
  // Legacy URN-prefixed keys
  'urn:polix:obligation:row-filter:table': 'table',
  'urn:polix:obligation:row-filter:condition': 'predicate',
  'urn:polix:obligation:mask-column:columns': 'column',
  'urn:polix:obligation:mask-column:mask-pattern': 'maskType',
  // Template-style keys (best-effort backward compat)
  'column-name': 'column',
  'mask-type': 'maskType',
  'mask-pattern': 'maskType',
}

/**
 * Parse XACML ObligationExpressions from a rule
 */
function parseXacmlObligations(
  obligationExpressions: Array<Record<string, unknown>> | null | undefined
): ParsedXacmlObligation[] {
  if (!obligationExpressions || !Array.isArray(obligationExpressions)) return []

  return obligationExpressions.map((oe) => {
    const rawObligationId = (oe.ObligationId as string) ?? ''
    const normalizedObligationId = OBLIGATION_ID_NORMALIZATION[rawObligationId] ?? rawObligationId

    const assignments = oe.AttributeAssignment as Array<Record<string, unknown>> | undefined
    const attributeAssignments: ParsedXacmlAttributeAssignment[] = (assignments ?? []).map((aa) => {
      const rawAttrId = (aa.AttributeId as string) ?? ''
      return {
        attributeId: OBLIGATION_ATTR_ID_NORMALIZATION[rawAttrId] ?? rawAttrId,
        value: (aa.Value as string | number | boolean) ?? '',
        dataType: aa.DataType as string | undefined,
      }
    })

    return {
      obligationId: normalizedObligationId,
      fulfillOn: (oe.FulfillOn as RuleEffect) ?? 'Permit',
      attributeAssignments,
    }
  })
}

/**
 * Parse XACML Rules array
 */
export function parseXacmlRules(
  rules: Array<Record<string, unknown>> | null | undefined
): ParsedXacmlRule[] {
  if (!rules || !Array.isArray(rules)) return []

  return rules.map((rule) => {
    const conditionRaw = rule.Condition as Record<string, unknown> | undefined
    const obligationExpressionsRaw = rule.ObligationExpressions as Array<Record<string, unknown>> | undefined
    return {
      ruleId: (rule.RuleId as string) ?? 'unknown',
      effect: (rule.Effect as RuleEffect) ?? 'Deny',
      description: rule.Description as string | undefined,
      target: rule.Target ? parseXacmlTarget(rule.Target as Record<string, unknown>) : undefined,
      conditions: parseXacmlCondition(conditionRaw),
      obligations: parseXacmlObligations(obligationExpressionsRaw),
      rawCondition: conditionRaw ? structuredClone(conditionRaw) : undefined,
      rawObligationExpressions: obligationExpressionsRaw ? structuredClone(obligationExpressionsRaw) : undefined,
    }
  })
}

/**
 * Parse full XACML policy to structured format
 */
export function parseXacmlPolicy(
  xacmlContent: Record<string, unknown> | null | undefined
): ParsedXacmlPolicy | null {
  if (!xacmlContent) return null

  // Handle both Policy and PolicySet structures
  const policy = (xacmlContent.Policy ?? xacmlContent) as Record<string, unknown>

  const policyId = (policy.PolicyId ?? policy['@PolicyId'] ?? 'unknown') as string
  const version = (policy.Version ?? policy['@Version'] ?? '1.0') as string
  const description = policy.Description as string | undefined
  const ruleCombiningAlgId = (policy.RuleCombiningAlgId ??
    policy['@RuleCombiningAlgId'] ??
    'urn:oasis:names:tc:xacml:3.0:rule-combining-algorithm:deny-overrides') as string

  // Parse target
  const target = parseXacmlTarget(policy.Target as Record<string, unknown> | undefined)

  // Parse rules - handle both single Rule and Rule array
  let rulesData = policy.Rule as Array<Record<string, unknown>> | Record<string, unknown> | undefined
  if (rulesData && !Array.isArray(rulesData)) {
    rulesData = [rulesData]
  }
  const rules = parseXacmlRules(rulesData as Array<Record<string, unknown>> | undefined)

  return {
    policyId,
    version,
    description,
    ruleCombiningAlgorithm: ruleCombiningAlgId,
    target,
    rules,
  }
}

/**
 * Check if parsed attributes have any values
 */
export function hasAttributes(attributes: ParsedXacmlAttributes): boolean {
  return (
    attributes.subject.length > 0 ||
    attributes.resource.length > 0 ||
    attributes.action.length > 0 ||
    attributes.environment.length > 0
  )
}

/**
 * Get total attribute count
 */
export function getAttributeCount(attributes: ParsedXacmlAttributes): number {
  return (
    attributes.subject.length +
    attributes.resource.length +
    attributes.action.length +
    attributes.environment.length
  )
}

/**
 * Get category attribute count
 */
export function getCategoryAttributeCount(
  attributes: ParsedXacmlAttributes,
  category: XacmlCategory
): number {
  return attributes[category].length
}
